import { createContext, useContext, useReducer } from 'react';

import { noop } from 'lodash';

import type { DutchieTemplate } from 'src/app/queries/email-marketing/dutchie-templates';
import type { MarketingTemplatePreset } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';

export enum CampaignStatus {
  DRAFT = 1,
  ARCHIVED = 2,
  SCHEDULED = 3,
  SENDING = 4,
  SENT = 5,
  ERROR = 6,
  CANCELED = 7,
  QUEUED = 10,
}

export enum AutomatedCampaignStatus {
  DRAFT = 1,
  ACTIVE = 8,
  INACTIVE = 9,
}

export enum CampaignType {
  AUTOMATED = 'automated',
  ONE_TIME_BLAST = 'one-time-blast',
}

export type EmailBuilderState = {
  isActive: boolean;
  marketingEmailId: number | null;
  marketingTemplatePresetId: number | null;
  metaSenderEmail: string | null;
  previewText: string;
  priority: number | null;
  senderEmail: string | null;
  subjectLine: string;
  utmTerm: string | null;
};

export type SMSBuilderState = {
  isActive: boolean;
  isMMS: boolean;
  leafbuyerImageTemplateId: string | null;
  leafbuyerMessageTemplateId: string | null;
  leafbuyerSMSMarketingConfigId: number | null;
  marketingSMSId: number | null;
  marketingTemplatePresetId: number | null;
  priority: number | null;
  utmTerm: string | null;
};

export type PushBuilderState = {
  body: string;
  isActive: boolean;
  marketingPushId: number | null;
  priority: number | null;
  redirectPath: string;
  title: string;
};

export type PointBoostBuilderState = {
  points: number;
};

export type CampaignBuilderState = {
  campaignName: string;
  dailyMaxEmailCapacity: number | null;
  delayInMinutes: number | null;
  delayUnitId: number | null;
  emailConfig: EmailBuilderState | null;
  emailsSentToday: number | null;
  eventTypeId: number | null;
  isWaterfall: boolean;
  lastModifiedDateUTC: string | null;
  marketingCampaignId: number | null;
  pointBoostConfig: PointBoostBuilderState | null;
  pushConfig: PushBuilderState | null;
  savedTemplateId: number | null;
  selectedSegments: number[];
  selectedTemplate?: DutchieTemplate | null;
  selectedTemplatePreset?: MarketingTemplatePreset | null;
  sendDate: string | null;
  sendToAll: boolean;
  smsConfig: SMSBuilderState | null;
  statusID: number;
  tagIds: number[];
  timeOfDayToSend: string | null;
  utmTerm: string | null;
};

export enum TimeUnitEnum {
  milliseconds = 1,
  seconds = 2,
  minutes = 3,
  hours = 4,
  days = 5,
  week = 6,
  month = 7,
  year = 8,
}

export type TimeUnit = keyof typeof TimeUnitEnum;

type UpdateFullCampaignAction = {
  payload: Omit<CampaignBuilderState, 'emailConfig' | 'smsConfig'> & {
    emailConfig: Omit<EmailBuilderState, 'selectedTemplate'> | null;
    smsConfig: Omit<SMSBuilderState, 'selectedTemplate'> | null;
  };
  type: 'update-full-campaign';
};

type ResetAction = {
  type: 'reset';
};

export type SetValueAction<K extends keyof CampaignBuilderState> = {
  payload: { key: K; value: CampaignBuilderState[K] };
  type: 'set-value';
};

export type SetEmailValueAction<K extends keyof EmailBuilderState> = {
  payload: { key: K; value: EmailBuilderState[K] };
  type: 'set-email-value';
};

export type SetSMSValueAction<K extends keyof SMSBuilderState> = {
  payload: { key: K; value: SMSBuilderState[K] };
  type: 'set-sms-value';
};

export type SetPushValueAction<K extends keyof PushBuilderState> = {
  payload: { key: K; value: PushBuilderState[K] };
  type: 'set-push-value';
};

export type SetPointBoostValueAction<K extends keyof PointBoostBuilderState> = {
  payload: { key: K; value: PointBoostBuilderState[K] };
  type: 'set-point-boost-value';
};

export type SetEmailActiveAction = {
  payload: boolean;
  type: 'set-email-active';
};

export type SetSMSActiveAction = {
  payload: boolean;
  type: 'set-sms-active';
};

export type SetPushActiveAction = {
  payload: boolean;
  type: 'set-push-active';
};

export type SetPointBoostActiveAction = {
  payload: boolean;
  type: 'set-point-boost-active';
};

export type SetWaterfallAction = {
  payload: boolean;
  type: 'set-waterfall';
};

export type SetChannelPriorityAction = {
  payload: { channel: 'email' | 'push' | 'sms'; priority: number | null };
  type: 'set-channel-priority';
};

export type Action =
  | ResetAction
  | SetChannelPriorityAction
  | SetEmailActiveAction
  | SetEmailValueAction<keyof EmailBuilderState>
  | SetPointBoostActiveAction
  | SetPointBoostValueAction<keyof PointBoostBuilderState>
  | SetPushActiveAction
  | SetPushValueAction<keyof PushBuilderState>
  | SetSMSActiveAction
  | SetSMSValueAction<keyof SMSBuilderState>
  | SetValueAction<keyof CampaignBuilderState>
  | SetWaterfallAction
  | UpdateFullCampaignAction;

export function builderReducer(state: CampaignBuilderState, action: Action): CampaignBuilderState {
  switch (action.type) {
    case 'set-value':
      return { ...state, [action.payload.key]: action.payload.value };
    case 'update-full-campaign':
      return { ...state, ...action.payload };
    case 'reset':
      return defaultBuilderState;
    case 'set-email-active':
      return { ...state, emailConfig: { ...(state.emailConfig || defaultEmailConfig), isActive: action.payload } };
    case 'set-sms-active':
      return { ...state, smsConfig: { ...(state.smsConfig || defaultSMSConfig), isActive: action.payload } };
    case 'set-push-active':
      return { ...state, pushConfig: { ...(state.pushConfig || defaultPushConfig), isActive: action.payload } };
    case 'set-point-boost-active':
      return {
        ...state,
        pointBoostConfig: { ...(state.pointBoostConfig || defaultPointBoostConfig) },
      };
    case 'set-email-value':
      return {
        ...state,
        emailConfig: { ...(state.emailConfig || defaultEmailConfig), [action.payload.key]: action.payload.value },
      };
    case 'set-sms-value':
      return {
        ...state,
        smsConfig: { ...(state.smsConfig || defaultSMSConfig), [action.payload.key]: action.payload.value },
      };
    case 'set-push-value':
      return {
        ...state,
        pushConfig: { ...(state.pushConfig || defaultPushConfig), [action.payload.key]: action.payload.value },
      };
    case 'set-waterfall':
      return { ...state, isWaterfall: action.payload };
    case 'set-channel-priority': {
      const { channel, priority } = action.payload;
      if (channel === 'email') {
        return {
          ...state,
          emailConfig: { ...(state.emailConfig || defaultEmailConfig), priority },
        };
      }
      if (channel === 'sms') {
        return {
          ...state,
          smsConfig: { ...(state.smsConfig || defaultSMSConfig), priority },
        };
      }
      if (channel === 'push') {
        return {
          ...state,
          pushConfig: { ...(state.pushConfig || defaultPushConfig), priority },
        };
      }
      return state;
    }
    default:
      return state;
  }
}

const defaultBuilderState: CampaignBuilderState = {
  marketingCampaignId: null,
  campaignName: '',
  emailsSentToday: null,
  dailyMaxEmailCapacity: null,
  delayInMinutes: null,
  delayUnitId: null,
  eventTypeId: null,
  lastModifiedDateUTC: null,
  emailConfig: null,
  smsConfig: null,
  pushConfig: null,
  pointBoostConfig: null,
  selectedSegments: [],
  sendDate: null,
  sendToAll: true,
  savedTemplateId: null,
  statusID: CampaignStatus.DRAFT,
  timeOfDayToSend: null,
  tagIds: [],
  utmTerm: null,
  selectedTemplate: null,
  selectedTemplatePreset: null,
  isWaterfall: false,
};

const defaultEmailConfig: EmailBuilderState = {
  marketingEmailId: null,
  marketingTemplatePresetId: null,
  subjectLine: '',
  previewText: '',
  isActive: false,
  priority: null,
  senderEmail: null,
  metaSenderEmail: null,
  utmTerm: null,
};

const defaultSMSConfig: SMSBuilderState = {
  marketingSMSId: null,
  leafbuyerSMSMarketingConfigId: null,
  leafbuyerMessageTemplateId: null,
  leafbuyerImageTemplateId: null,
  isActive: false,
  priority: null,
  marketingTemplatePresetId: null,
  isMMS: false,
  utmTerm: null,
};

const defaultPushConfig: PushBuilderState = {
  marketingPushId: null,
  title: '',
  body: '',
  redirectPath: '/', // homepage
  isActive: false,
  priority: null,
};

const defaultPointBoostConfig: PointBoostBuilderState = {
  points: 0,
};

export function useBuilderReducer() {
  return useReducer(builderReducer, defaultBuilderState);
}

export const BuilderContext = createContext<ReturnType<typeof useBuilderReducer>>([defaultBuilderState, noop]);

export function useBuilderContext() {
  return useContext(BuilderContext);
}
